// src/pages/finance/Invoices.jsx
// SRS Section 4.2.3 - Invoice management page
// Finance Manager can view all invoices, generate new ones, and send via email.

import React, { useState, useEffect } from 'react';
import {
  FileText, Loader2, Download, Send,
  Plus, X, AlertCircle, CheckCircle2
} from 'lucide-react';
import { financeService } from '../../services/financeService';
import { useAuth } from '../../context/AuthContext';

// ── Generate Invoice Modal ───────────────────────────────────────────────
const GenerateInvoiceModal = ({ onClose, onDone }) => {
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState('');
  const [selectedMilestone, setSelectedMilestone] = useState('');
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await financeService.getContracts();
        setContracts(res.data || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleContractChange = (contractId) => {
    setSelectedContract(contractId);
    setSelectedMilestone('');
    const contract = contracts.find(c => c.contract_id === contractId);
    if (contract) {
      const pending = (contract.milestones || []).filter(m => m.status !== 'Received');
      setMilestones(pending);
    } else {
      setMilestones([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedContract || !selectedMilestone) {
      setError('Please select contract and milestone');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await financeService.generateInvoice(selectedContract, selectedMilestone);
      onDone();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-md">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between font-bold text-gray-900">
          Generate Invoice
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-primary opacity-30" />
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Contract</label>
                <select
                  value={selectedContract}
                  onChange={(e) => handleContractChange(e.target.value)}
                  required
                  className="w-full p-2.5 border rounded-xl bg-gray-50 text-sm"
                >
                  <option value="">Select Contract...</option>
                  {contracts.map(c => (
                    <option key={c.contract_id} value={c.contract_id}>
                      {c.project_name} — {c.client_name}
                    </option>
                  ))}
                </select>
              </div>
              {milestones.length > 0 && (
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Milestone</label>
                  <select
                    value={selectedMilestone}
                    onChange={(e) => setSelectedMilestone(e.target.value)}
                    required
                    className="w-full p-2.5 border rounded-xl bg-gray-50 text-sm"
                  >
                    <option value="">Select Milestone...</option>
                    {milestones.map(m => (
                      <option key={m.milestone_id} value={m.milestone_id}>
                        {m.description} — {m.amount.toLocaleString()}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {selectedContract && milestones.length === 0 && (
                <p className="text-xs text-amber-600 bg-amber-50 p-3 rounded-lg">
                  All milestones for this contract are already received.
                </p>
              )}
            </>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-gray-500 border border-gray-200">
              Cancel
            </button>
            <button type="submit" disabled={submitting || loading}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-primary flex items-center justify-center gap-2 disabled:opacity-60">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Generate
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Send Invoice Modal ────────────────────────────────────────────────────
const SendInvoiceModal = ({ invoice, onClose, onDone }) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await financeService.sendInvoice(invoice.invoice_id, email);
      onDone();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-sm">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between font-bold text-gray-900">
          Send Invoice via Email
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}
          <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-600">
            <p className="font-semibold">{invoice.project_name}</p>
            <p className="text-xs mt-0.5">{invoice.client_name} — {invoice.amount?.toLocaleString()}</p>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Client Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="client@example.com"
              className="w-full p-2.5 border rounded-xl bg-gray-50 text-sm"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-gray-500 border border-gray-200">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-primary flex items-center justify-center gap-2">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Main Invoices Page ────────────────────────────────────────────────────
const Invoices = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showGenerate, setShowGenerate] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  const isAdmin = user && ['super_admin', 'finance_manager'].includes(user.role);

  const loadInvoices = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await financeService.getInvoices();
      setInvoices(res.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadInvoices(); }, []);

  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(''), 3000);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  const handleDownload = (invoiceId) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
    const token = localStorage.getItem('access_token');
    window.open(baseUrl + '/api/finance/invoices/' + invoiceId + '/download?token=' + token, '_blank');
  };

  return (
    <div className="space-y-6 pb-10">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Invoices</h1>
          <p className="text-sm text-gray-500 mt-0.5">Generate and send client invoices</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowGenerate(true)}
            className="flex items-center gap-1.5 bg-primary hover:bg-primary-dark text-white px-4 py-2.5 rounded-control text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Generate Invoice
          </button>
        )}
      </div>

      {successMsg && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-green-800 text-sm">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          {successMsg}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary opacity-20" />
        </div>
      ) : invoices.length === 0 ? (
        <div className="bg-white rounded-card border border-dashed border-gray-200 p-12 text-center">
          <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No invoices yet. Generate one from a contract milestone.</p>
        </div>
      ) : (
        <div className="bg-white rounded-card border border-gray-100 shadow-soft overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs font-bold text-gray-400 uppercase tracking-wide">
                <th className="px-5 py-3">Invoice ID</th>
                <th className="px-5 py-3">Client</th>
                <th className="px-5 py-3">Project</th>
                <th className="px-5 py-3">Description</th>
                <th className="px-5 py-3">Amount</th>
                <th className="px-5 py-3">Sent</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3.5 font-mono text-xs text-gray-500">{inv.invoice_id}</td>
                  <td className="px-5 py-3.5 font-medium text-gray-800">{inv.client_name}</td>
                  <td className="px-5 py-3.5 text-gray-600">{inv.project_name}</td>
                  <td className="px-5 py-3.5 text-gray-600 max-w-xs truncate">{inv.description}</td>
                  <td className="px-5 py-3.5 font-bold text-gray-900">{inv.amount?.toLocaleString()} {inv.currency}</td>
                  <td className="px-5 py-3.5">
                    {inv.sent_at
                      ? <span className="text-xs text-green-600 font-bold">Sent</span>
                      : <span className="text-xs text-gray-400">Not sent</span>}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => handleDownload(inv.invoice_id)}
                        className="p-1.5 text-gray-400 hover:text-primary border border-gray-100 rounded-lg hover:border-primary/20 transition-colors"
                        title="Download PDF"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => setSendingInvoice(inv)}
                          className="p-1.5 text-gray-400 hover:text-green-600 border border-gray-100 rounded-lg hover:border-green-200 transition-colors"
                          title="Send to client"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showGenerate && (
        <GenerateInvoiceModal
          onClose={() => setShowGenerate(false)}
          onDone={() => {
            setShowGenerate(false);
            setSuccessMsg('Invoice generated successfully');
            loadInvoices();
          }}
        />
      )}

      {sendingInvoice && (
        <SendInvoiceModal
          invoice={sendingInvoice}
          onClose={() => setSendingInvoice(null)}
          onDone={() => {
            setSendingInvoice(null);
            setSuccessMsg('Invoice sent to client successfully');
            loadInvoices();
          }}
        />
      )}
    </div>
  );
};

export default Invoices;